import React, { useState, useEffect, useRef } from 'react';
import EmrConfigService from '../../../services/EmrConfigService';
import VisitsService from '../../../services/VisitsService';
import EmrVisitsService from '../../../services/EmrVisitsService';
import EmrStatusBar from '../Tabs/ComplaintPage/EmrStatusBar';
import { OK } from '../../../utils/constant';

const HistoryRecapSection = (props) => {
  const { visit_id, selectedSubTab, Toast, visitDetails, Loader, profile, fetchVisitList } = props;
  const providerDetails = visitDetails?.primaryProvider || visitDetails?.["0"]?.primaryProvider || visitDetails?.primary_provider || visitDetails?.["0"]?.primary_provider;
  const providerDisplayName = providerDetails
    ? `${providerDetails.first_name || ''} ${providerDetails.last_name || ''}`.trim()
    : "-";
  const visitRecord = visitDetails?.["0"] ?? visitDetails;
  const historyRecapScrollRef = useRef(null);
  const [attestationChecked, setAttestationChecked] = useState(!!visitRecord?.history_review_attestation);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [historyData, setHistoryData] = useState({
    hpi: '',
    pmh: '',
    ros: ''
  });
  const [allNormalMessages, setAllNormalMessages] = useState([]);
  const [allNormalMessagesLoaded, setAllNormalMessagesLoaded] = useState(false);

  const SCROLL_THRESHOLD = 80;
  const checkIfScrollable = () => {
    const el = historyRecapScrollRef.current;
    if (!el) return;
    setShowScrollButton(el.scrollHeight > el.clientHeight);
  };

  useEffect(() => {
    const el = historyRecapScrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => checkIfScrollable());
    observer.observe(el);
    const t1 = setTimeout(checkIfScrollable, 100);
    const t2 = setTimeout(checkIfScrollable, 400);
    return () => {
      observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [historyData.hpi, historyData.pmh, historyData.ros]);

  useEffect(() => {
    const el = historyRecapScrollRef.current;
    if (!el || !showScrollButton) return;
    const onScroll = () => {
      setIsScrolledDown(el.scrollTop > SCROLL_THRESHOLD);
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [showScrollButton]);

  const scrollToBottom = () => {
    if (historyRecapScrollRef.current) {
      historyRecapScrollRef.current.scrollTo({
        top: historyRecapScrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    if (historyRecapScrollRef.current) {
      historyRecapScrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    if (visit_id) {
      setAllNormalMessagesLoaded(false);
      fetchAllNormalMessages();
      fetchProgressNotes();
    }
  },[visit_id, selectedSubTab]);

  useEffect(() => {
    setAttestationChecked(!!visitRecord?.history_review_attestation);
  }, [visitRecord?.history_review_attestation]);

  const handleAttestationChange = async (e) => {
    const checked = e.target.checked;
    setAttestationChecked(checked);
    try {
      const payload = {
        history_review_attestation: checked
      };
      await VisitsService.updateHistoryAttestation(visit_id, payload);
      Toast?.success?.(checked ? 'Provider Attestation saved.' : 'Provider Attestation cleared.');
      if (typeof fetchVisitList === 'function') {
        fetchVisitList();
      }
    } catch (err) {
      console.error('Error saving attestation:', err);
      setAttestationChecked(!checked);
      Toast?.error?.('Failed to save attestation.');
    }
  };

  const normalizeText = (text) =>
    String(text || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[.,;:!?]+$/g, '')
      .toLowerCase();

  const fetchAllNormalMessages = async () => {
    Loader(true);
    try {
      const screenTypes = ['HPI', 'PMH', 'ROS'];
      const responses = await Promise.all(
        screenTypes.map(async (screenType) => {
          try {
            const response = await EmrVisitsService.getEmrChartByScreen(screenType, visit_id, { is_active: 1 });
            const list = Array.isArray(response?.data) ? response.data : [];
            return list
              .map((item) => normalizeText(item?.all_normal_text))
              .filter(Boolean);
          } catch (err) {
            console.error(`Error fetching all normal messages for ${screenType}:`, err);
            return [];
          }
        })
      );
      setAllNormalMessages(Array.from(new Set(responses.flat())));
    } catch (err) {
      console.error('Error fetching all normal messages:', err);
      setAllNormalMessages([]);
    } finally {
      setAllNormalMessagesLoaded(true);
      Loader(false);
    }
  };

  // Exclude lines where value is "Normal"  keep only positive/abnormal findings
  const filterToPositiveFindingsOnly = (htmlText) => {
    if (!htmlText || !String(htmlText).trim()) return '';
    const wrapLine = (html) => `<div class="recap-line">${html}</div>`;
    const isConfiguredAllNormalMessage = (text) => {
      if (!allNormalMessagesLoaded) return false;
      const normalizedLine = normalizeText(text);
      if (!normalizedLine || allNormalMessages.length === 0) return false;
      const valuePart = normalizedLine.includes(':')
        ? normalizedLine.substring(normalizedLine.indexOf(':') + 1).trim()
        : normalizedLine;
      return allNormalMessages.some((msg) => msg === normalizedLine || msg === valuePart);
    };
    const isNormalOrNegative = (text) => {
      const t = (text || '').trim();
      if (!t) return true;
      if (/:\s*(normal|negative)\s*$/i.test(t) || /^\s*(normal|negative)\s*$/i.test(t)) return true;
      if (/:\s*NONE\b/i.test(t)) return true;
      if (/^NONE\b/i.test(t)) return true;
      if (isConfiguredAllNormalMessage(t)) return true;
      return false;
    };

    const removeNoneFromLine = (html) => {
      const tempEl = document.createElement('div');
      tempEl.innerHTML = html;
      const text = (tempEl.textContent || tempEl.innerText || '').trim();
      
      const colonIndex = text.indexOf(':');
      
      if (colonIndex !== -1) {
        const label = text.substring(0, colonIndex + 1);
        const valuesPart = text.substring(colonIndex + 1).trim();
        const values = valuesPart.split(',').map(v => v.trim()).filter(v => v && !/^none\b/i.test(v));
        
        if (values.length === 0) {
          return '';
        }
        return `<strong>${label}</strong> ${values.join(', ')}`;
      }
      
      const noneMatch = text.match(/^(.+?)\s+NONE\b/i);
      if (noneMatch) {
        const label = noneMatch[1].trim();
        const afterNone = text.substring(noneMatch[0].length);
        const remaining = afterNone.replace(/^\s*\([^)]*\)\s*,?\s*/, '').trim();
        
        if (remaining) {
          const values = remaining.split(',').map(v => v.trim()).filter(v => v && !/^none\b/i.test(v));
          if (values.length > 0) {
            return `<strong>${label}:</strong> ${values.join(', ')}`;
          }
        }
        return '';
      }
      
      return html;
    };

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText;
    const divElements = tempDiv.querySelectorAll('div[data-field-id]');
    if (divElements.length) {
      const resultParts = [];
      divElements.forEach((div) => {
        const rawContent = div.innerHTML || '';
        const lineParts = rawContent.split(/<br\s*\/?>/gi);
        const keptLines = lineParts
          .map(removeNoneFromLine)
          .filter((part) => {
            const inner = document.createElement('div');
            inner.innerHTML = part;
            const text = (inner.textContent || inner.innerText || '').trim();
            return text && !isNormalOrNegative(text);
          });
        if (keptLines.length) {
          resultParts.push(`<div data-field-id="${div.getAttribute('data-field-id') || ''}">${keptLines.map(wrapLine).join('')}</div>`);
        }
      });
      return resultParts.join('');
    }
    const parts = htmlText.split(/<br\s*\/?>/gi);
    const kept = parts
      .map(removeNoneFromLine)
      .filter((part) => {
        const inner = document.createElement('div');
        inner.innerHTML = part;
        const text = (inner.textContent || inner.innerText || '').trim();
        return text && !isNormalOrNegative(text);
      });
    return kept.map(wrapLine).join('');
  };

  const fetchProgressNotes = async () => {
    try {
      Loader(true);
      const screenTypes = ['HPI', 'PMH', 'ROS'];
      const responses = await Promise.all(
        screenTypes.map(async (screenType) => {
          try {
            const response = await EmrConfigService.visifetchProgressData(visit_id, screenType);
            if (response && (response.code === OK || response.code === 200 || response.status === 'SUCCESS')) {
              const displayValue = response.data?.display_value || '';
              return { screenType, html: displayValue };
            }
            return { screenType, html: '' };
          } catch (err) {
            console.error(`Error fetching progress note for ${screenType}:`, err);
            return { screenType, html: '' };
          }
        })
      );
      setHistoryData({
        hpi: responses.find(r => r.screenType === 'HPI')?.html ?? '',
        pmh: responses.find(r => r.screenType === 'PMH')?.html ?? '',
        ros: responses.find(r => r.screenType === 'ROS')?.html ?? ''
      });
    } catch (err) {
      console.error('Error fetching progress notes:', err);
    } finally {
      Loader(false);
    }
  };

  const renderSection = (title, html) => {
    const filtered = filterToPositiveFindingsOnly(html);
    const isEmpty = !filtered || !filtered.trim();
    return (
      <div  key={title}>
        <div className="card-header p-2 sticky-top" style={{ top: '-1px', zIndex: 0 }}>
          <h6 className="mb-0 font-weight-bold text-sm">{title}</h6>
        </div>
        <div
          className="card-body p-2 text-sm text-dark recap-progress-content"
          style={{ overflowX: 'hidden', wordWrap: 'break-word', minHeight: '200px' }}
        >
          {isEmpty ? (
            <p className="text-dark mb-0">No Abnormal Findings Documented</p>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: filtered }} />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`.recap-progress-content .recap-line { margin-bottom: 0.5rem; }`}</style>
    <div
    className="sh_cardHeader mb-2 rounded-2"
    style={{
      background: "linear-gradient(90deg, #FFF7DC 0%, #F7E9E5 100%)",
    }}
  >
    <div className="d-flex justify-content-center">
      <div className="form-group d-flex mb-0 justify-content-between align-items-center">
        <EmrStatusBar visitDetails={props.visitDetails} />
      </div>
    </div>
  </div>
      <div>
        <p className="text-dark  text-sm"><strong className="text-dark font-weight-bold p-2 text-sm">History Summary:</strong>This sections includes positive findings only.Normal/negative findings are excluded.</p>
      </div>
    <div className="position-relative">
      <div ref={historyRecapScrollRef} className="sh_cardBody p-0 position-relative" style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '69vh' }}>
      {renderSection('History of Present Illness', historyData.hpi)}
      
      {/* Past Medical History Section */}
      {renderSection('Past Medical History', historyData.pmh)}
      
      {/* Review of Systems Section */}
      {renderSection('Review of Systems', historyData.ros)}
      <div className="mb-3">
        <div 
          className="card-header p-2 sticky-top" 
          style={{ 
            top: '-1px', 
            zIndex: 0,
          }}  
        >
          <h6 className="mb-0 font-weight-bold text-sm">Provider Attestation</h6>
          </div>
          <div className="card-body p-2">
            <div className="d-flex flex-row align-items-start">
              <div className="form-check mr-3">
                <input
                  className="form-check-input sh_checkboxnew"
                  type="checkbox"
                  id="attestation"
                  checked={attestationChecked}
                  onChange={handleAttestationChange}
                />
                <label className="form-check-label text-sm text-dark" htmlFor="attestation" style={{ cursor: 'pointer' }}>
                  I reviewed the patient's history before initiating today's evaluation and management, and updated it as appropriate.
                </label>
              </div>
              <div className="ml-auto text-right">
                <div className="text-sm text-dark mb-1">
                  <strong>Reviewed By:</strong> {providerDisplayName || '—'}
                </div>
                <div className="text-sm text-dark">
                  <strong>Date:</strong> {visitRecord?.history_review_attestation_date 
                    ? new Date(visitRecord.history_review_attestation_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : '-'}
                </div>
              </div>
            </div>
          </div>

      </div>
      </div>
      {showScrollButton && (
        <div
          className="d-flex justify-content-center pt-2 pb-1 sticky-top bg-white"
          style={{ flexShrink: 0, zIndex: 1, top: 0 }}
        >
          <button
            type="button"
            onClick={isScrolledDown ? scrollToTop : scrollToBottom}
            className="border rounded-circle bg-white d-flex align-items-center justify-content-center p-0 shadow-sm"
            style={{
              width: 36,
              height: 36,
              borderColor: '#c4c4c4',
              borderWidth: '1.5px',
              cursor: 'pointer',
              flexShrink: 0
            }}
            aria-label={isScrolledDown ? 'Scroll to top' : 'Scroll to bottom'}
          >
            {isScrolledDown ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 19V5M5 12l7-7 7 7" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5v14M19 12l-7 7-7-7" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
    
    </>
  );
};

export default HistoryRecapSection;

